import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatDate } from '../lib/api';
import { useAuth } from '../lib/auth';
import { StatusBadge, PriorityBadge, SourceBadge } from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, MessageSquare, Send, GitBranch, ArrowRightLeft, XCircle, CheckCircle, MessageCircle } from 'lucide-react';
import FileUpload from '../components/FileUpload';
import VoicePlayer from '../components/VoicePlayer';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function WorkRequestDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [wr, setWr] = useState(null);
  const [comments, setComments] = useState([]);
  const [voiceNoteUrl, setVoiceNoteUrl] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');

  useEffect(() => { load(); }, [id]);

  const load = async () => {
    try {
      const [wrRes, commRes, attRes] = await Promise.all([
        api.get(`/work-requests/${id}`),
        api.get(`/work-requests/${id}/comments`),
        api.get(`/attachments?entity_type=work_request&entity_id=${id}`),
      ]);
      setWr(wrRes.data);
      setComments(commRes.data);
      // Find voice note attachment (audio/* mime type)
      const audioAtt = attRes.data.find(a => a.mime_type?.startsWith('audio/'));
      setVoiceNoteUrl(audioAtt?.download_url || null);
    } catch (err) {
      toast.error('Failed to load work request');
      navigate('/work-requests');
    } finally { setLoading(false); }
  };

  const doAction = async (action, label) => {
    setActionLoading(action);
    try {
      await api.post(`/work-requests/${id}/${action}`);
      toast.success(`Request ${label}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${label}`);
    } finally { setActionLoading(''); }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      const { data } = await api.post(`/work-requests/${id}/comments`, { text: newComment });
      setComments(prev => [...prev, data]);
      setNewComment('');
    } catch (err) { toast.error('Failed to add comment'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Loading...</div>;
  if (!wr) return null;

  const canAct = ['admin', 'supervisor', 'super_admin'].includes(user?.role);
  const isActionable = ['NEW', 'UNDER_REVIEW', 'CONVERTED_TO_WO'].includes(wr.status);
  const canMessageRequester = canAct && wr.created_by && wr.created_by !== user?.id;

  const handleMessageRequester = async () => {
    if (!wr.created_by) return;
    try {
      toast.info(t('chat.startingChat'));
      const { data } = await api.post('/chat/threads', {
        type: 'private',
        title: `${t('chat.chatWith', { name: wr.created_by_name })} - WR#${wr.id.slice(0, 8)}`,
        participant_user_id: wr.created_by
      });
      navigate(`/chat?thread=${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    }
  };

  return (
    <div data-testid="wr-detail-page" className="space-y-6 max-w-4xl">
      <Button variant="ghost" size="sm" onClick={() => navigate('/work-requests')} data-testid="wr-back-btn" className="text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4 me-1" /> Back to Requests
      </Button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="flex-1">
          <h1 className="font-barlow font-bold text-2xl text-foreground tracking-tight">{wr.title || `Request ${wr.id.slice(0,8)}`}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <StatusBadge status={wr.status} />
            <PriorityBadge priority={wr.priority} />
            <span className="text-xs text-muted-foreground">ID: {wr.id.slice(0, 8)}</span>
          </div>
        </div>
        {/* Actions */}
        {canAct && isActionable && (
          <div className="flex flex-wrap gap-2">
            {wr.status === 'NEW' && (
              <Button size="sm" variant="outline" onClick={() => doAction('review', 'marked for review')} disabled={!!actionLoading} data-testid="wr-review-btn">
                <GitBranch className="w-3.5 h-3.5 me-1" /> Review
              </Button>
            )}
            <Button size="sm" className="bg-brand hover:bg-brand-hover text-brand-foreground text-white" onClick={() => doAction('convert', 'converted to work order')} disabled={!!actionLoading} data-testid="wr-convert-btn">
              <ArrowRightLeft className="w-3.5 h-3.5 me-1" /> Convert to WO
            </Button>
            <Button size="sm" variant="outline" className="text-rose-600 hover:bg-rose-50 border-rose-200" onClick={() => doAction('reject', 'rejected')} disabled={!!actionLoading} data-testid="wr-reject-btn">
              <XCircle className="w-3.5 h-3.5 me-1" /> Reject
            </Button>
            <Button size="sm" variant="outline" onClick={() => doAction('close', 'closed')} disabled={!!actionLoading} data-testid="wr-close-btn">
              <CheckCircle className="w-3.5 h-3.5 me-1" /> Close
            </Button>
          </div>
        )}
        {canMessageRequester && (
          <Button size="sm" variant="outline" onClick={handleMessageRequester} data-testid="wr-message-requester-btn">
            <MessageCircle className="w-3.5 h-3.5 me-1" /> {t('chat.messageRequester')}
          </Button>
        )}
      </div>

      {/* Details */}
      <Card className="bg-card border-border shadow-sm">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Branch</p>
              <p className="text-sm text-foreground mt-0.5">{wr.branch_name || wr.branch_id}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Created By</p>
              <p className="text-sm text-foreground mt-0.5">{wr.created_by_name}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Room</p>
              <p className="text-sm text-foreground mt-0.5" data-testid="wr-detail-room">
                {wr.room_name || <span className="text-muted-foreground italic">{t('common.noRoom')}</span>}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Asset</p>
              <p className="text-sm text-foreground mt-0.5">{(i18n.language === 'ar' && wr.asset_name_ar) ? wr.asset_name_ar : (wr.asset_name || '\u2014')}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Created</p>
              <p className="text-sm text-foreground mt-0.5">{formatDate(wr.created_at)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Updated</p>
              <p className="text-sm text-foreground mt-0.5">{formatDate(wr.updated_at)}</p>
            </div>
          </div>
          <Separator className="my-4" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Description</p>
            {wr.description && (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{wr.description}</p>
            )}
            {voiceNoteUrl && (
              <div className="mt-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Voice Note</p>
                <VoicePlayer url={voiceNoteUrl} />
              </div>
            )}
            {!wr.description && !voiceNoteUrl && (
              <p className="text-sm text-muted-foreground">—</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Maintenance progress (read-only tracking, requester safe) */}
      {wr.work_order_id && (
        <Card className="bg-card border-border shadow-sm" data-testid="wr-progress-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
              {t('myMaintenance.timeline')}
              <span className="font-mono text-xs text-brand">{wr.work_order_id.slice(0, 8)}</span>
              {wr.maintenance_source && <SourceBadge source={wr.maintenance_source} compact />}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-y-4 gap-x-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.status')}</p>
                <div className="mt-1" data-testid="wr-wo-status"><StatusBadge status={wr.wo_status} /></div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('workOrder.assignedTo')}</p>
                <p className="text-sm text-foreground mt-0.5" data-testid="wr-wo-technician">
                  {wr.wo_assigned_to_name || t('workOrder.unassigned')}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('myMaintenance.startedAt')}</p>
                <p className="text-sm text-foreground mt-0.5">{wr.wo_started_at ? formatDate(wr.wo_started_at) : t('myMaintenance.notStarted')}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('myMaintenance.closedAt')}</p>
                <p className="text-sm text-foreground mt-0.5">{wr.wo_closed_at ? formatDate(wr.wo_closed_at) : '\u2014'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('myMaintenance.duration')}</p>
                <p className="text-sm text-foreground mt-0.5">{wr.wo_duration_minutes != null ? `${wr.wo_duration_minutes} min` : '\u2014'}</p>
              </div>
            </div>

            {wr.wo_resolution_text && (
              <>
                <Separator className="my-4" />
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{t('myMaintenance.completionResult')}</p>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{wr.wo_resolution_text}</p>
              </>
            )}

            {wr.wo_timeline?.length > 0 && (
              <>
                <Separator className="my-4" />
                <ol className="space-y-3" data-testid="wr-wo-timeline">
                  {wr.wo_timeline.map((h, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand flex-shrink-0" />
                      <div className="flex flex-wrap items-center gap-2">
                        {h.from_status && <StatusBadge status={h.from_status} />}
                        <span className="text-muted-foreground text-xs">&rarr;</span>
                        <StatusBadge status={h.to_status} />
                        <span className="text-xs text-muted-foreground">{formatDate(h.timestamp)}</span>
                      </div>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Attachments */}
      <Card className="bg-card border-border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-barlow text-lg text-foreground">Attachments</CardTitle>
        </CardHeader>
        <CardContent>
          <FileUpload entityType="work_request" entityId={id} />
        </CardContent>
      </Card>

      {/* Comments */}
      <Card className="bg-card border-border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Comments ({comments.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {comments.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">No comments yet</p>
          )}
          {comments.map(c => (
            <div key={c.id} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-semibold text-muted-foreground flex-shrink-0">
                {c.user_name?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-foreground">{c.user_name}</span>
                  <span className="text-xs text-muted-foreground">{formatDate(c.created_at)}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">{c.text}</p>
              </div>
            </div>
          ))}
          <Separator />
          <form onSubmit={addComment} className="flex gap-2" data-testid="wr-comment-form">
            <Textarea
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              rows={2}
              className="flex-1"
              data-testid="wr-comment-input"
            />
            <Button type="submit" size="sm" className="self-end bg-brand hover:bg-brand-hover text-brand-foreground" disabled={!newComment.trim()} data-testid="wr-comment-submit">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
